import {
  isOrderEligibleForReconfirm,
  isOrderReconfirmed,
  isOrderWithPriceChangesEdited,
} from './deal-reconfirm.utils';
import {
  isDealStatusAllowedForDraftSubmit,
  isDealStatusAllowedForReconfirmSubmit,
  isDealStatusAllowedForSubmitOrCancel,
  isOrderPeriodOutsideWindow,
  isRepriceWindow,
} from '../deal-series/deal-status';
import { isOrderPassesSubmissionAmountRules } from '../deal-series/deal-series.util';
import {
  isOrderActionableForReconfirmSubmit,
  isOrderValidForCancellation,
  isOrderValidForSubmission,
} from './deal-submit-preview/deal-submit-preview-grid.utils';
import { MaturityOrder } from '../deal-series/deal-series.types';

export interface DealActionEligibilityInput {
  dealStatus: string | null | undefined;
  orderPeriodBeginDate: string | null | undefined;
  orderPeriodEndDate: string | null | undefined;
  orders: MaturityOrder[];
}

export interface DealActionEligibility {
  submitEnabled: boolean;
  cancelEnabled: boolean;
  reconfirmEnabled: boolean;
  orderPeriodOutsideWindow: boolean;
}

/**
 * Single source of truth for deal-level Submit / Cancel / Reconfirm header enablement.
 * Deal status, order-period window, and per-order rules are evaluated here only.
 */
export const computeDealActionEligibility = ({
  dealStatus,
  orderPeriodBeginDate,
  orderPeriodEndDate,
  orders,
}: DealActionEligibilityInput): DealActionEligibility => {
  const orderPeriodOutsideWindow = isOrderPeriodOutsideWindow(
    dealStatus,
    orderPeriodBeginDate,
    orderPeriodEndDate,
  );

  if (!isDealStatusAllowedForSubmitOrCancel(dealStatus)) {
    return {
      submitEnabled: false,
      cancelEnabled: false,
      reconfirmEnabled: false,
      orderPeriodOutsideWindow,
    };
  }

  const isInRepriceWindow = isRepriceWindow(dealStatus);

  let hasSubmitCandidate = false;
  let hasCancelCandidate = false;
  let hasReconfirmActionableCandidate = false;
  let hasUserEditedPriceChangeOrders = false;

  for (const order of orders) {
    if (!isOrderReconfirmed(order) && isOrderValidForSubmission(order)) {
      hasSubmitCandidate = true;
    }
    if (isOrderValidForCancellation(order)) {
      if (!isInRepriceWindow || isOrderEligibleForReconfirm(order)) {
        hasCancelCandidate = true;
      }
    }
    if (isOrderEligibleForReconfirm(order) && isOrderPassesSubmissionAmountRules(order)) {
      hasReconfirmActionableCandidate = true;
    }
    if (isOrderWithPriceChangesEdited(order)) {
      hasUserEditedPriceChangeOrders = true;
    }
  }

  const hasOrdersToSubmit =
    !isInRepriceWindow || orders.some((o) => isOrderActionableForReconfirmSubmit(o));

  const draftSubmitAllowedForDealStatus = isDealStatusAllowedForDraftSubmit(
    dealStatus,
    orderPeriodBeginDate,
    orderPeriodEndDate,
  );

  let submitEnabled = isInRepriceWindow
    ? hasOrdersToSubmit
    : draftSubmitAllowedForDealStatus && hasSubmitCandidate && hasOrdersToSubmit;

  const reconfirmEnabled =
    isDealStatusAllowedForReconfirmSubmit(dealStatus) &&
    hasReconfirmActionableCandidate &&
    !hasUserEditedPriceChangeOrders;

  if (reconfirmEnabled) {
    submitEnabled = false;
  }

  return {
    submitEnabled,
    cancelEnabled: hasCancelCandidate && !orderPeriodOutsideWindow,
    reconfirmEnabled,
    orderPeriodOutsideWindow,
  };
};
